import { PageHero } from "@/components/page-hero";
import { PageLayout } from "@/components/page-layout";
import { ROLE_GUIDES, type RoleGuide } from "@/content/role-guides";
import { breadcrumbSchema, faqSchema, webPageSchema } from "@/utils/schema";
import { jsonLdScript, seo } from "@/utils/seo";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@super-resume-new/ui/components/table";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  ArrowRight,
  Briefcase,
  CheckCircle2,
  FileText,
  ListChecks,
  Sparkles,
  TriangleAlert,
  Wand2,
} from "lucide-react";

export const Route = createFileRoute("/resume/$role")({
  // Imported here rather than at the top of the file on purpose. The route
  // definition — loader, head — is in the entry chunk, the component is not, so
  // a static import of the guides puts 56 kB of copy on every page of the site.
  // Awaiting it here loads it on this route only.
  loader: async ({ params }) => {
    const { ROLE_GUIDE_BY_SLUG } = await import("@/content/role-guides");
    const guide = ROLE_GUIDE_BY_SLUG.get(params.role);
    if (!guide) throw notFound();
    return guide;
  },
  head: ({ loaderData }) => {
    const guide = loaderData as RoleGuide | undefined;
    if (!guide) {
      return seo({
        title: "Resume guide not found | JobCues",
        description: "Browse the JobCues resume guides by role.",
        noindex: true,
      });
    }
    const path = `/resume/${guide.slug}`;
    return {
      ...seo({
        title: guide.metaTitle,
        description: guide.metaDescription,
        path,
        type: "article",
        publishedTime: guide.updated,
        modifiedTime: guide.updated,
      }),
      scripts: [
        jsonLdScript(
          webPageSchema({
            name: guide.metaTitle,
            description: guide.metaDescription,
            path,
            datePublished: guide.updated,
            dateModified: guide.updated,
          }),
        ),
        jsonLdScript(faqSchema(guide.faqs)),
        jsonLdScript(
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Resume Guides", path: "/resume" },
            { name: `${guide.name} Resume`, path },
          ]),
        ),
      ],
    };
  },
  component: RoleGuideComponent,
});

function Section({
  id,
  title,
  icon: Icon,
  children,
}: {
  id: string;
  title: string;
  icon: typeof Wand2;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-12 scroll-mt-20">
      <h2 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-foreground">
        <Icon className="size-4 text-brand-foreground" aria-hidden="true" />
        {title}
      </h2>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function RoleGuideComponent() {
  const guide = Route.useLoaderData();
  const related = ROLE_GUIDES.filter((g) => g.slug !== guide.slug).slice(0, 6);

  return (
    <PageLayout className="max-w-4xl">
      <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
        <ol className="flex flex-wrap items-center gap-1.5">
          <li>
            <Link to="/" className="hover:text-foreground">
              Home
            </Link>
          </li>
          <li aria-hidden="true">/</li>
          <li>
            <Link to="/resume" className="hover:text-foreground">
              Resume Guides
            </Link>
          </li>
          <li aria-hidden="true">/</li>
          <li className="text-foreground">{guide.name} Resume</li>
        </ol>
      </nav>

      <PageHero
        eyebrow="Resume Guide"
        icon={FileText}
        title={`${guide.name} Resume`}
        description={`Updated ${new Date(guide.updated).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })} · written against live ${guide.name.toLowerCase()} postings on JobCues`}
      />

      {/* The self-contained answer. First thing on the page, because roughly
          half of AI citations come from the opening of a document. */}
      <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
        <h2 className="text-base font-semibold text-foreground">
          What does an ATS look for on a {guide.name.toLowerCase()} resume?
        </h2>
        <p className="mt-2 text-base leading-relaxed text-muted-foreground">{guide.definition}</p>
      </div>

      <Section id="how-ats-reads" title="How an ATS reads this resume" icon={ListChecks}>
        <div className="space-y-3">
          {guide.atsNotes.map((note) => (
            <p key={note} className="text-base leading-relaxed text-muted-foreground">
              {note}
            </p>
          ))}
        </div>
      </Section>

      <Section id="keywords" title={`${guide.name} resume keywords`} icon={Sparkles}>
        <p className="text-base leading-relaxed text-muted-foreground">
          These are the terms that recur across {guide.name.toLowerCase()} postings. A scanner
          matches them as literal strings, so spelling and casing carry more weight than they
          should. Only claim what you can defend.
        </p>
        <h3 className="mt-5 text-sm font-semibold text-foreground">Technical terms</h3>
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {guide.hardKeywords.map((k) => (
            <li key={k}>
              <Badge variant="secondary" className="font-normal">
                {k}
              </Badge>
            </li>
          ))}
        </ul>
        <h3 className="mt-5 text-sm font-semibold text-foreground">Working-practice terms</h3>
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {guide.softKeywords.map((k) => (
            <li key={k}>
              <Badge variant="outline" className="font-normal">
                {k}
              </Badge>
            </li>
          ))}
        </ul>
      </Section>

      <Section id="parent-terms" title="Parent terms a scanner never infers" icon={TriangleAlert}>
        <p className="text-base leading-relaxed text-muted-foreground">
          A keyword scanner matches letters. It does not know that one of these implies the other,
          so a resume that names only the left column fails a posting written with the right one.
          Writing both is the cheapest coverage gain available.
        </p>
        <div className="mt-4 overflow-hidden rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>You wrote</TableHead>
                <TableHead>The posting asks for</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {guide.parentTerms.map((t) => (
                <TableRow key={t.child}>
                  <TableCell className="font-medium">{t.child}</TableCell>
                  <TableCell className="text-muted-foreground">{t.parent}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Section>

      <Section id="bullets" title="Before and after bullets" icon={Wand2}>
        <div className="space-y-4">
          {guide.bullets.map((b) => (
            <div key={b.before} className="rounded-xl border border-border bg-card p-4 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Before
              </p>
              <p className="mt-1 text-base text-muted-foreground line-through decoration-red-500/40">
                {b.before}
              </p>
              <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-brand-foreground">
                After
              </p>
              <p className="mt-1 text-base leading-relaxed text-foreground">{b.after}</p>
              <p className="mt-3 border-t border-border pt-3 text-sm leading-relaxed text-muted-foreground">
                {b.why}
              </p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="section-order" title="Section order" icon={ListChecks}>
        <ol className="flex flex-wrap items-center gap-2">
          {guide.sectionOrder.map((s, i) => (
            <li key={s} className="flex items-center gap-2">
              <Badge variant="secondary" className="font-normal">
                {i + 1}. {s}
              </Badge>
              {i < guide.sectionOrder.length - 1 ? (
                <ArrowRight className="size-3 text-muted-foreground" aria-hidden="true" />
              ) : null}
            </li>
          ))}
        </ol>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          {guide.sectionRationale}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          <span className="font-medium text-foreground">Summary is optional.</span> Use it to
          clear a hard requirement the posting states: work authorisation, a named language level
          (JLPT N2, IELTS 7), security clearance, willingness to relocate, a required licence, or
          a notice period. If you want to add one anyway, keep it to one line about you or your
          work, then anything that genuinely catches a recruiter's eye. But we recommend putting
          that energy into the first few sections instead and making those count.
        </p>
      </Section>

      <Section id="mistakes" title="Common mistakes" icon={TriangleAlert}>
        <div className="space-y-4">
          {guide.mistakes.map((m) => (
            <div key={m.title}>
              <h3 className="text-base font-semibold text-foreground">{m.title}</h3>
              <p className="mt-1 text-base leading-relaxed text-muted-foreground">{m.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="faq" title="Frequently asked questions" icon={CheckCircle2}>
        <div className="space-y-5">
          {guide.faqs.map((f) => (
            <div key={f.q}>
              <h3 className="text-base font-semibold text-foreground">{f.q}</h3>
              <p className="mt-1 text-base leading-relaxed text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      <section className="mt-12 rounded-xl border border-border bg-card p-5 shadow-sm">
        <h2 className="text-base font-semibold text-foreground">
          Run this against a real {guide.name.toLowerCase()} posting
        </h2>
        <p className="mt-2 text-base leading-relaxed text-muted-foreground">
          The lists above are the general case. Every posting has its own keyword set, and the only
          score that matters is the one against the job you are applying to. Paste the description
          and get the matched terms, the missing terms, and a rewritten resume in about 15 seconds.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/resume-tailor">
            <Button className="cursor-pointer">
              <Wand2 className="size-4" />
              Tailor my resume
            </Button>
          </Link>
          <Link to="/jobs" search={{ q: guide.jobsQuery }}>
            <Button variant="outline" className="cursor-pointer">
              <Briefcase className="size-4" />
              Browse {guide.name.toLowerCase()} jobs
            </Button>
          </Link>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="text-sm font-semibold text-foreground">Other resume guides</h2>
        <ul className="mt-3 flex flex-wrap gap-2">
          {related.map((g) => (
            <li key={g.slug}>
              <Link
                to="/resume/$role"
                params={{ role: g.slug }}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                {g.name} resume
                <ArrowRight className="size-3" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </PageLayout>
  );
}
